import { DEFAULT_LANG } from '@dehaat/kisan-app-bl/constants/common';
import { ProductVariantV3 } from '@dehaat/kisan-app-bl/models/ProductV3';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { MouseEventHandler, useContext, useState } from 'react';
import { CART_TYPE } from '@/constants/cart';
import { AVAILABLE_STOCK } from '@/constants/common';
import { CartContext } from '@/context/CartProvider';
import { useAppSelector } from '@/hooks/reduxHooks';
import useTriggerEvents from '@/hooks/useTriggerEvents';
import { Product, ProductVariant } from '@/models/Product';
import { selectCurrentVendor } from '@/store/features/locationSlice';
import CustomEventTypes from '@/utils/analytics/customEventTypes';
import { ProductListTypeEnum } from '@/utils/analytics/types';
import addNotifyMe from '@/utils/api/addNotifyMe';
import removeWishlist from '@/utils/api/removeWishlist';
import { isShriProgramProduct, productCatalogueId } from '@/utils/cart';
import { getCookieValue } from '@/utils/helper';
import { getCartUpdatePricing } from '@/utils/helpers/flashSale';
import ButtonLabel, { VARIANT } from '../ButtonLabel';
import NotifyMeIcon from '../icons/NotifyMeIcon';
import { Toast } from '../Toast';
import { requiredPathName } from './AddToCart2';
import HyperLocalAddRemoveButton from './HyperLocalAddRemoveButton';
interface Props {
  btnContainerStyle?: string;
  index?: number;
  listType?: ProductListTypeEnum;
  minQty?: number;
  productVariant: ProductVariant | ProductVariantV3;
  maxQty?: number;
  isDrawerVisible?: boolean;
  fullWidthCta?: boolean;
  isOutOfStock?: boolean;
  length?: number;
  onClick?: () => void;
  quantity?: number;
  componentName?: string;
  componentId?: string | number;
  isWishlist?: boolean;
  product?: Product;
  onShowSimilar?: () => void;
}
interface NotifyMeProps {
  product?: Product;
  locale?: string;
  isWishlist?: boolean;
  isDrawerVisible: boolean;
}
const HyperLocalAddToCart = ({
  btnContainerStyle,
  index,
  listType,
  minQty,
  productVariant,
  isDrawerVisible = false,
  fullWidthCta = false,
  isOutOfStock,
  length = 1,
  onClick,
  quantity: computedQuantity,
  componentName = '',
  componentId = '',
  isWishlist,
  product,
  onShowSimilar
}: Props) => {
  const {
    t
  } = useTranslation('products');
  const router = useRouter();
  const {
    cartItems,
    updateCart,
    cartType
  } = useContext(CartContext);
  const stopBubbling: MouseEventHandler<HTMLElement> = e => {
    e.preventDefault();
    e.stopPropagation();
  };
  const {
    trackCustomEvent
  } = useTriggerEvents();
  const vendorDetails = useAppSelector(selectCurrentVendor);
  const userId = getCookieValue('user_id');
  const {
    locale
  } = useRouter();
  const StockInSS = typeof window !== 'undefined' && sessionStorage?.getItem(AVAILABLE_STOCK);
  const availableStock = StockInSS ? JSON.parse(StockInSS) : undefined;
  const quantity = computedQuantity ?? (cartItems[productVariant?.id] || 0);
  const drawerCtaWidth = isDrawerVisible ? fullWidthCta ? 'w-full' : 'w-32' : 'w-full flex-1';
  const disableRemove = !(typeof minQty === 'undefined' || quantity > minQty);
  const disableEditQuantity = isShriProgramProduct(productVariant?.id, false);
  const diasbleAdd = length > 1 ? false : availableStock && availableStock[productVariant?.id] !== 'undefined' && quantity >= availableStock[productVariant?.id];
  const createEventParams = () => {
    return {
      ProductName: (productVariant as ProductVariant).product?.name + ' ' + (productVariant as ProductVariant).attribute_value[0]?.name,
      ProductVariant: (productVariant as ProductVariant).id,
      ProductBrand: (productVariant as ProductVariant).product?.brand?.name,
      ProductPrice: (productVariant as ProductVariant).inventory_set[0].price,
      Page: router.asPath,
      VendorName: vendorDetails?.company_name,
      VendorId: vendorDetails?.id,
      Attribute: (productVariant as ProductVariant).attribute_value[0].name
    };
  };
  const onAddOrRemoveFromCart = (isAdd: boolean) => {
    let name, brandName;
    if (cartType === CART_TYPE.DEHAAT) {
      name = (productVariant as ProductVariant).product?.name;
      brandName = (productVariant as ProductVariant).product?.brand?.name;
    } else {
      name = (productVariant as ProductVariantV3).name;
      brandName = (productVariant as ProductVariantV3).brand.name;
    }
    const inventorySet = productVariant.inventory_set;
    const pricing = getCartUpdatePricing(productVariant as ProductVariant);
    updateCart({
      variantId: productVariant.id,
      quantity: 1,
      index,
      type: listType,
      ...pricing,
      name,
      brandName,
      catalogueId: productCatalogueId(inventorySet),
      groupId: {
        parent_id: (productVariant as ProductVariant).product?.parent_id,
        parent_type: (productVariant as ProductVariant).product?.parent_type
      }
    }, isAdd);
  };
  const SimilarCtaEventParams = () => {
    return {
      ProductName: product?.name,
      ScreenName: router.asPath,
      ProductCategory: product?.category?.name,
      ProductBrand: product?.brand?.name,
      VendorName: vendorDetails?.company_name,
      VendorId: vendorDetails?.id
    };
  };
  const NotifyMe = ({
    product,
    locale,
    isWishlist,
    isDrawerVisible
  }: NotifyMeProps) => {
    const {
      t
    } = useTranslation('products');
    const [isNotified, setIsNotified] = useState(product?.is_notify || false);
    const [isToast, setIsToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const notifyEventProps = {
      VendorName: vendorDetails?.company_name,
      VendorId: vendorDetails?.id,
      ProductName: product?.name,
      ProductBrand: product?.brand?.name,
      ProductCategory: product?.category?.name,
      PageLocation: router.asPath,
      PageName: requiredPathName(router.asPath)
    };
    const handleAddNotify = async () => {
      try {
        const result = await addNotifyMe({
          productId: Number(product?.id),
          locale: locale || DEFAULT_LANG,
          triggerSource: isWishlist ? 'WISHLIST' : 'NOTIFYME'
        });
        if (result?.success) {
          setToastMessage(t('notify_me_available'));
          setIsToast(true);
          setIsNotified(true);
          trackCustomEvent(CustomEventTypes.USER_CLICKED_NOTIFY_ME_CTA, JSON.stringify(notifyEventProps));
        } else if (!result.success && result.message.includes('Unauthorized')) {
          setToastMessage(t('login_to_add_notification'));
          setIsToast(true);
        }
      } catch (error) {
        console.error('Failed to add notify me:', error);
      }
    };
    return <button className={`rounded-lg py-2.5 justify-center items-center mt-2 bg-white border border-gray-30 flex h-11 absolute  bottom-[8px] right-[8px] left-[8px] ${fullWidthCta ? 'w-full' : isDrawerVisible ? 'w-32' : 'flex-1'}`} onClick={e => {
      stopBubbling(e);
      if (isNotified) {
        setToastMessage(t('notify_me_requested'));
        setIsToast(true);
      } else {
        handleAddNotify();
      }
    }} data-sentry-component="NotifyMe" data-sentry-source-file="HyperLocalAddToCart.tsx">
        <>
          {isToast && <Toast message={toastMessage} onClose={() => setIsToast(false)} duration={4000} />}

          {isNotified ? <NotifyMeIcon width={27} height={27} /> : <ButtonLabel variant={VARIANT.TWO} className="font-nato-semibold text-primary-100" label={t('notify_me')} />}
        </>
      </button>;
  };
  return isWishlist && isOutOfStock ? <div data-sentry-component="HyperLocalAddToCart" data-sentry-source-file="HyperLocalAddToCart.tsx">
      <button className={`rounded-lg py-2.5 justify-center items-center mt-2 ${drawerCtaWidth}
    bg-primary-20 border border-primary-100`} onClick={e => {
      stopBubbling(e);
      trackCustomEvent(CustomEventTypes.WISHLIST_USER_CLICKED_SHOW_SIMILAR_CTA, JSON.stringify(SimilarCtaEventParams()));
      router.push(`/products/categories/${encodeURIComponent(product?.category?.kheti_slug || '')}`);
    }}>
        <ButtonLabel variant={VARIANT.TWO} className="font-nato-semibold text-primary-100" label={t('similar_products')} data-sentry-element="ButtonLabel" data-sentry-source-file="HyperLocalAddToCart.tsx" />
      </button>
      <NotifyMe product={product} locale={locale} isWishlist={isWishlist} isDrawerVisible={isDrawerVisible} data-sentry-element="NotifyMe" data-sentry-source-file="HyperLocalAddToCart.tsx" />
    </div> : quantity === 0 ? isOutOfStock ? <NotifyMe product={product} locale={locale} isWishlist={isWishlist} isDrawerVisible={isDrawerVisible} /> : <button className={`rounded-lg py-2.5 justify-center items-center mt-2 ${drawerCtaWidth} bg-primary-100`} onClick={e => {
    if (!isOutOfStock) {
      if (length > 1) {
        onClick?.();
      } else {
        onShowSimilar?.();
        isWishlist && removeWishlist({
          userId: userId,
          productId: Number(product?.id),
          operation: 'MOVED_TO_CART'
        });
        isWishlist && trackCustomEvent(CustomEventTypes.WISHLIST_PRODUCT_MOVED_TO_CART, JSON.stringify(SimilarCtaEventParams()));
        onAddOrRemoveFromCart(true);
        const eventParam = createEventParams();
        if (eventParam.VendorId && eventParam.VendorName) {
          trackCustomEvent(CustomEventTypes.ADD_TO_CART, JSON.stringify({
            componentId: componentId,
            componentName: componentName,
            ...eventParam
          }));
        }
      }
      stopBubbling(e);
    }
  }}>
        <ButtonLabel variant={VARIANT.TWO} className="ml-2 font-nato-semibold text-white" label={t('add')} />
      </button> : <HyperLocalAddRemoveButton btnContainerStyle={disableEditQuantity ? '' : btnContainerStyle} disableRemove={disableRemove || disableEditQuantity} disableAdd={diasbleAdd || disableEditQuantity} fullWidthCta={fullWidthCta} onAdd={() => {
    if (length > 1) onClick?.();else {
      const eventParam = createEventParams();
      trackCustomEvent(CustomEventTypes.ADD_ONE_ITEM_TO_CART, JSON.stringify(eventParam));
      onAddOrRemoveFromCart(true);
    }
  }} onRemove={() => {
    if (length > 1) onClick?.();else {
      const eventParam = createEventParams();
      trackCustomEvent(CustomEventTypes.REMOVE_ONE_ITEM_FROM_CART, JSON.stringify(eventParam));
      if (!disableRemove) {
        onAddOrRemoveFromCart(false);
      }
    }
  }} quantity={quantity} isDrawerVisible={isDrawerVisible} />;
};
export default HyperLocalAddToCart;